/**
 * useValidation Hook
 *
 * Custom hook for form validation logic with debouncing and real-time validation.
 * Provides comprehensive validation state management for the provider registration workflow.
 * Updated to follow patterns from useDieticianValidation.ts with useCallback for all validation functions.
 *
 * @module hooks/useValidation
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { validateEmail as validateEmailUtil } from '@/utils/validation/emailValidation';
import { validatePassword as validatePasswordUtil } from '@/utils/validation/passwordValidation';
import { validatePhoneNumber as validatePhoneUtil } from '@/utils/validation/phoneValidation';
import { validateProviderNumber as validateProviderNumberUtil } from '@/utils/validation/providerNumberValidation';
import { VALID_PROVIDER_ROLES } from '@/constants';
import type { ValidationResult, FieldValidation } from '@/types/registration';

/**
 * Validation configuration interface
 */
export interface ValidationConfig {
  /** Debounce delay in milliseconds */
  debounceMs?: number;
  /** Whether to validate on change */
  validateOnChange?: boolean;
  /** Whether to validate on blur */
  validateOnBlur?: boolean;
  /** Whether to show errors immediately */
  showErrorsImmediately?: boolean;
}

/**
 * Field validation state map
 */
export interface ValidationState {
  [fieldName: string]: FieldValidation;
}

/**
 * Validation context type
 */
export interface ValidationContext {
  password?: string;
  country?: string;
  countryCode?: string;
  isoCode?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  [key: string]: unknown;
}

/**
 * useValidation hook return type
 */
export interface UseValidationReturn {
  /** Current validation state for all fields */
  validationState: ValidationState;
  /** Validate a specific field */
  validateField: (fieldName: string, value: string | File, context?: ValidationContext) => Promise<ValidationResult>;
  /** Validate all fields */
  validateAll: (values: Record<string, unknown>) => Promise<boolean>;
  /** Clear validation for a field */
  clearValidation: (fieldName: string) => void;
  /** Clear all validation */
  clearAll: () => void;
  /** Set field as touched */
  setFieldTouched: (fieldName: string, touched?: boolean) => void;
  /** Check if form is valid */
  isFormValid: boolean;
  /** Get field validation result */
  getFieldValidation: (fieldName: string) => FieldValidation;
}

/**
 * Default validation configuration
 */
const DEFAULT_CONFIG: ValidationConfig = {
  debounceMs: 300,
  validateOnChange: true,
  validateOnBlur: true,
  showErrorsImmediately: false
};

/**
 * Default field validation state
 */
const createDefaultFieldValidation = (): FieldValidation => ({
  result: { isValid: true },
  touched: false,
  validating: false
});

/**
 * Custom hook for form validation with debouncing and real-time feedback
 *
 * @param fieldNames - Array of field names to validate
 * @param config - Validation configuration
 * @returns Validation state and methods
 *
 * @example
 * ```typescript
 * const {
 *   validationState,
 *   validateField,
 *   validateAll,
 *   isFormValid
 * } = useValidation(['email', 'password', 'confirmPassword']);
 *
 * // Validate a field
 * await validateField('email', 'user@example.com');
 *
 * // Validate all fields
 * const isValid = await validateAll({
 *   email: 'user@example.com',
 *   password: 'password123',
 *   confirmPassword: 'password123'
 * });
 * ```
 */
export const useValidation = (
  fieldNames: string[],
  config: ValidationConfig = {}
): UseValidationReturn => {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };

  // Initialize validation state for all fields
  const [validationState, setValidationState] = useState<ValidationState>(() => {
    const initialState: ValidationState = {};
    fieldNames.forEach(fieldName => {
      initialState[fieldName] = createDefaultFieldValidation();
    });
    return initialState;
  });

  // Debounce timers
  const debounceTimers = useRef<Record<string, NodeJS.Timeout>>({});

  // Clear timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach(timer => clearTimeout(timer));
    };
  }, []);

  // Built-in validation functions using useCallback pattern like useDieticianValidation.ts
  const validateEmailField = useCallback((value: string): ValidationResult => {
    return validateEmailUtil(value);
  }, []);

  const validatePasswordField = useCallback((value: string): ValidationResult => {
    return validatePasswordUtil(value);
  }, []);

  const validateConfirmPassword = useCallback((value: string, context?: { password?: string }): ValidationResult => {
    if (!value) {
      return { isValid: false, error: 'Please confirm your password' };
    }
    if (value !== context?.password) {
      return { isValid: false, error: 'Passwords do not match' };
    }
    return { isValid: true };
  }, []);

  const validateFirstName = useCallback((value: string): ValidationResult => {
    if (!value || value.trim().length === 0) {
      return { isValid: false, error: 'First name is required' };
    }
    if (value.trim().length < 1 || value.trim().length > 100) {
      return { isValid: false, error: 'First name must be between 1 and 100 characters' };
    }
    if (!/^[a-zA-Z\s\-']+$/.test(value.trim())) {
      return { isValid: false, error: 'First name can only contain letters, spaces, hyphens, and apostrophes' };
    }
    return { isValid: true };
  }, []);

  const validateLastName = useCallback((value: string): ValidationResult => {
    if (!value || value.trim().length === 0) {
      return { isValid: false, error: 'Last name is required' };
    }
    if (value.trim().length < 1 || value.trim().length > 100) {
      return { isValid: false, error: 'Last name must be between 1 and 100 characters' };
    }
    if (!/^[a-zA-Z\s\-']+$/.test(value.trim())) {
      return { isValid: false, error: 'Last name can only contain letters, spaces, hyphens, and apostrophes' };
    }
    return { isValid: true };
  }, []);

  const validateRole = useCallback((value: string): ValidationResult => {
    if (!value || value.trim().length === 0) {
      return { isValid: false, error: 'Role is required' };
    }
    if (!VALID_PROVIDER_ROLES.includes(value as 'gp' | 'specialist' | 'surgeon' | 'nurse')) {
      return { isValid: false, error: 'Invalid role' };
    }
    return { isValid: true };
  }, []);

  const validateProviderNumberField = useCallback((value: string, context?: { country?: string }): ValidationResult => {
    return validateProviderNumberUtil(value, context?.country || 'AU');
  }, []);

  const validatePracticeName = useCallback((value: string): ValidationResult => {
    if (!value || value.trim().length === 0) {
      return { isValid: false, error: 'Practice name is required' };
    }
    if (value.trim().length < 1 || value.trim().length > 100) {
      return { isValid: false, error: 'Practice name must be between 1 and 100 characters' };
    }
    return { isValid: true };
  }, []);

  const validatePracticeAddress = useCallback((value: string): ValidationResult => {
    if (!value || value.trim().length === 0) {
      return { isValid: false, error: 'Practice address is required' };
    }
    if (value.trim().length < 5 || value.trim().length > 200) {
      return { isValid: false, error: 'Practice address must be between 5 and 200 characters' };
    }
    // Basic address validation - should contain some alphanumeric characters
    if (!/^[a-zA-Z0-9\s\-,.'#/]+$/.test(value.trim())) {
      return { isValid: false, error: 'Practice address contains invalid characters' };
    }
    return { isValid: true };
  }, []);

  const validateBio = useCallback((value: string): ValidationResult => {
    // Bio is optional, so empty is valid
    if (!value || value.trim().length === 0) {
      return { isValid: true };
    }
    if (value.trim().length > 500) {
      return { isValid: false, error: 'Bio must be 500 characters or less' };
    }
    return { isValid: true };
  }, []);

  const validatePhoneField = useCallback((value: string, context?: { countryCode?: string; isoCode?: string }): ValidationResult => {
    if (!value || value.trim().length === 0) {
      return { isValid: false, error: 'Phone number is required' };
    }
    return validatePhoneUtil({
      countryCode: context?.countryCode || '+61',
      isoCode: context?.isoCode || 'AU',
      number: value,
      formatted: value,
      international: `${context?.countryCode || '+61'} ${value}`
    });
  }, []);

  const validatePhoto = useCallback((file: File): ValidationResult => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];

    if (file.size > maxSize) {
      return { isValid: false, error: 'Photo must be less than 5MB' };
    }

    if (!allowedTypes.includes(file.type)) {
      return { isValid: false, error: 'Photo must be JPEG or PNG format' };
    }

    return { isValid: true };
  }, []);

  /**
   * Get validation function for a field name
   */
  const getValidationFunction = useCallback((fieldName: string) => {
    switch (fieldName) {
      case 'email':
        return validateEmailField;
      case 'password':
        return validatePasswordField;
      case 'confirmPassword':
        return validateConfirmPassword;
      case 'firstName':
        return validateFirstName;
      case 'lastName':
        return validateLastName;
      case 'role':
        return validateRole;
      case 'providerNumber':
        return validateProviderNumberField;
      case 'practiceName':
        return validatePracticeName;
      case 'practiceAddress':
        return validatePracticeAddress;
      case 'bio':
        return validateBio;
      case 'phone':
        return validatePhoneField;
      case 'photo':
        return validatePhoto;
      default:
        return null;
    }
  }, [
    validateEmailField,
    validatePasswordField,
    validateConfirmPassword,
    validateFirstName,
    validateLastName,
    validateRole,
    validateProviderNumberField,
    validatePracticeName,
    validatePracticeAddress,
    validateBio,
    validatePhoneField,
    validatePhoto
  ]);

  /**
   * Update validation state for a specific field
   */
  const updateFieldValidation = useCallback((
    fieldName: string,
    updates: Partial<FieldValidation>
  ) => {
    setValidationState(prev => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        ...updates
      }
    }));
  }, []);

  /**
   * Validate a specific field
   */
  const validateField = useCallback(async (
    fieldName: string,
    value: string | File,
    context?: ValidationContext
  ): Promise<ValidationResult> => {
    // Clear existing debounce timer
    if (debounceTimers.current[fieldName]) {
      clearTimeout(debounceTimers.current[fieldName]);
    }

    // Set validating state
    updateFieldValidation(fieldName, { validating: true });

    return new Promise((resolve) => {
      debounceTimers.current[fieldName] = setTimeout(() => {
        const validationFn = getValidationFunction(fieldName);

        if (!validationFn) {
          const result = { isValid: true };
          updateFieldValidation(fieldName, {
            result,
            validating: false
          });
          resolve(result);
          return;
        }

        try {
          // Handle special case for photo validation which expects File
          const result = fieldName === 'photo' && value instanceof File
            ? (validationFn as (file: File) => ValidationResult)(value)
            : (validationFn as (value: string, context?: ValidationContext) => ValidationResult)(value as string, context);

          updateFieldValidation(fieldName, {
            result,
            validating: false
          });
          resolve(result);
        } catch {
          const result = {
            isValid: false,
            error: 'Validation error occurred'
          };
          updateFieldValidation(fieldName, {
            result,
            validating: false
          });
          resolve(result);
        }
      }, mergedConfig.debounceMs);
    });
  }, [updateFieldValidation, mergedConfig.debounceMs, getValidationFunction]);

  /**
   * Validate all fields
   */
  const validateAll = useCallback(async (
    values: Record<string, unknown>
  ): Promise<boolean> => {
    const validationPromises = fieldNames.map(async (fieldName) => {
      const value = fieldName === 'photo' && values[fieldName] instanceof File
        ? values[fieldName] as File
        : String(values[fieldName] || '');
      const context = values as ValidationContext; // Pass all values as context
      return validateField(fieldName, value, context);
    });

    const results = await Promise.all(validationPromises);
    return results.every(result => result.isValid);
  }, [fieldNames, validateField]);

  /**
   * Clear validation for a specific field
   */
  const clearValidation = useCallback((fieldName: string) => {
    if (debounceTimers.current[fieldName]) {
      clearTimeout(debounceTimers.current[fieldName]);
    }
    updateFieldValidation(fieldName, createDefaultFieldValidation());
  }, [updateFieldValidation]);

  /**
   * Clear all validation
   */
  const clearAll = useCallback(() => {
    Object.values(debounceTimers.current).forEach(timer => clearTimeout(timer));
    debounceTimers.current = {};

    const clearedState: ValidationState = {};
    fieldNames.forEach(fieldName => {
      clearedState[fieldName] = createDefaultFieldValidation();
    });
    setValidationState(clearedState);
  }, [fieldNames]);

  /**
   * Set field as touched
   */
  const setFieldTouched = useCallback((fieldName: string, touched = true) => {
    updateFieldValidation(fieldName, { touched });
  }, [updateFieldValidation]);

  /**
   * Get field validation result
   */
  const getFieldValidation = useCallback((fieldName: string): FieldValidation => {
    return validationState[fieldName] || createDefaultFieldValidation();
  }, [validationState]);

  /**
   * Check if form is valid
   */
  const isFormValid = Object.values(validationState).every(
    field => field.result.isValid && !field.validating
  );

  return {
    validationState,
    validateField,
    validateAll,
    clearValidation,
    clearAll,
    setFieldTouched,
    isFormValid,
    getFieldValidation
  };
};

export default useValidation;